import { useEffect, useRef, useState, type ReactNode } from 'react';
import type {
  ReportFormatResponse,
  ReportFormatSettings,
  RoundingAccountOption,
} from '@/core/models/firm-settings.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { firmSettingsService } from '@/core/services/firmSettings';
import { Button } from '@/kit/Button';
import { ConfirmPopup, type ConfirmPopupData } from '@/kit/ConfirmPopup';
import { openDialog } from '@/kit/dialog';
import { InputField } from '@/kit/FormField';
import { useDirtyForm } from '@/kit/hooks/useDirtyForm';
import { Icon } from '@/kit/Icon';
import { InfoHint } from '@/kit/InfoHint';
import { LeaveGuard } from '@/kit/LeaveGuard';
import { PageHeader, PageHeaderFooter } from '@/kit/PageHeader';
import { Select } from '@/kit/Select';
import { TabGroup } from '@/kit/Tabs';
import { Toggle } from '@/kit/Toggle';
import { validateForm } from './validateForm';

const ROUNDING_SLOTS = 5;
const WHOLE_NUMBER = /^\d+$/;
const MAX_NUMBER = 999;
const DIGITS_ONLY_MESSAGE = 'Please enter only digits';
const NUMBER_FIELDS = [
  'titleFontSize',
  'tableHeaderFontSize',
  'reportBodyFontSize',
  'headerFontSize',
  'noteBodyPadding',
  'textHeadingPadding',
  'marginTop',
  'marginBottom',
  'marginLeft',
  'marginRight',
] as const;
const FLAG_FIELDS = [
  'printPageNumberInPdf',
  'printPageNumberOnCoverPage',
  'doubleUnderlineTotals',
  'showDashForZero',
  'hidePeriodStartOnCoverPage',
  'printRegistrationNumberOnBalanceSheet',
  'autoSaveJournals',
  'manageAutoRounding',
] as const;

type NumberField = (typeof NUMBER_FIELDS)[number];
type FlagField = (typeof FLAG_FIELDS)[number];

interface FormValues {
  fontName: string | null;
  headerAlignment: string | null;
  numbers: Record<NumberField, string>;
  flags: Record<FlagField, boolean>;
  plRounding: (string | null)[];
  bsRounding: (string | null)[];
}

function emptyValues(): FormValues {
  return {
    fontName: null,
    headerAlignment: null,
    numbers: Object.fromEntries(NUMBER_FIELDS.map((field) => [field, ''])) as Record<NumberField, string>,
    flags: Object.fromEntries(FLAG_FIELDS.map((field) => [field, false])) as Record<FlagField, boolean>,
    plRounding: Array.from({ length: ROUNDING_SLOTS }, () => null),
    bsRounding: Array.from({ length: ROUNDING_SLOTS }, () => null),
  };
}

/** Wording of the API's own range check (FirmPreferenceService.CheckRange), so the page and the server agree. */
const RANGE_LABELS: Record<NumberField, { label: string; unit: string }> = {
  titleFontSize: { label: 'Title font size', unit: 'px' },
  tableHeaderFontSize: { label: 'Table header font size', unit: 'px' },
  reportBodyFontSize: { label: 'Report body font size', unit: 'px' },
  headerFontSize: { label: 'Header font size', unit: 'px' },
  noteBodyPadding: { label: 'Padding for note body', unit: 'px' },
  textHeadingPadding: { label: 'Padding for text and heading', unit: 'px' },
  marginTop: { label: 'Top margin', unit: 'pt' },
  marginBottom: { label: 'Bottom margin', unit: 'pt' },
  marginLeft: { label: 'Left margin', unit: 'pt' },
  marginRight: { label: 'Right margin', unit: 'pt' },
};

/**
 * BUG-013: the box keeps what was typed ('1.5' is no longer stripped to '15'), so a bad value is refused here with a
 * message instead. Empty is the mandatory case (no inline text, the generic toast), anything else is digits 0-999.
 */
function numberError(field: NumberField, raw: string): string | null {
  if (raw.length === 0) return null;
  if (!WHOLE_NUMBER.test(raw)) return DIGITS_ONLY_MESSAGE;
  if (Number(raw) > MAX_NUMBER) {
    const { label, unit } = RANGE_LABELS[field];
    return `${label} must be between 0 and ${MAX_NUMBER} ${unit}.`;
  }
  return null;
}

function numberInvalid(field: NumberField, raw: string): boolean {
  return raw.length === 0 || numberError(field, raw) !== null;
}

function requiredInvalid(value: string | null): boolean {
  return value === null || value === undefined || value === '';
}

function toOptions(accounts: RoundingAccountOption[]) {
  return accounts.map((account) => ({ value: account.code, label: `${account.code} - ${account.name}` }));
}

const SECTION_TITLE = 'form-field-title hr-top mt-10 mb-1 pt-1';

/**
 * BUG-012: `.emoji-sec .highlight-details` floats a page's single Edit/Save link to the top-right corner. This page
 * shows Save beside Cancel in its own bar, where the floated Save covered half of Cancel, so they stay in the bar.
 */

/**
 * Firm-wide report formatting and journal/rounding settings. Design review 2 Nov 2026: always editable - once a setting
 * differs from what was saved the header offers Save (persists them) and Discard (puts the saved ones back).
 * With a `clientId` it is that client's own Report Format (BUG-074), which overrides the firm's for its reports.
 */
export function Reportformat({
  clientId,
  onClose,
  intro,
}: { clientId?: string; onClose?: () => void; intro?: string } = {}) {
  const { values, setValues, dirty, discard, markSaved } = useDirtyForm<FormValues>(emptyValues());
  const [fonts, setFonts] = useState<string[]>([]);
  const [headerAlignments, setHeaderAlignments] = useState<string[]>([]);
  const [profitAndLossAccounts, setProfitAndLossAccounts] = useState<RoundingAccountOption[]>([]);
  const [balanceSheetAccounts, setBalanceSheetAccounts] = useState<RoundingAccountOption[]>([]);
  /** Angular `touched` per control; `all` is `markAllAsTouched()`. */
  const [touched, setTouched] = useState<{ all: boolean; fields: Set<string> }>({ all: false, fields: new Set() });
  const [isCustomised, setIsCustomised] = useState(false);
  const mounted = useRef(true);

  const apply = (response: ReportFormatResponse) => {
    setIsCustomised(response.isCustomised);
    setFonts(response.options.fonts);
    setHeaderAlignments(response.options.headerAlignments);
    setProfitAndLossAccounts(response.options.profitAndLossAccounts);
    setBalanceSheetAccounts(response.options.balanceSheetAccounts);

    const settings = response.settings;
    markSaved({
      fontName: settings.fontName,
      headerAlignment: settings.headerAlignment,
      numbers: Object.fromEntries(NUMBER_FIELDS.map((field) => [field, String(settings[field])])) as Record<
        NumberField,
        string
      >,
      flags: Object.fromEntries(FLAG_FIELDS.map((field) => [field, settings[field]])) as Record<FlagField, boolean>,
      plRounding: Array.from({ length: ROUNDING_SLOTS }, (_, i) => settings.profitAndLossRoundingAccounts?.[i] ?? null),
      bsRounding: Array.from({ length: ROUNDING_SLOTS }, (_, i) => settings.balanceSheetRoundingAccounts?.[i] ?? null),
    });
  };

  const load = () => {
    (clientId ? firmSettingsService.getClientReportFormat(clientId) : firmSettingsService.getReportFormat()).then(
      (response) => {
        if (mounted.current) apply(response);
      },
      (error) => notifyError(error),
    );
  };

  useEffect(() => {
    mounted.current = true;
    load();
    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load reads the clientId this effect depends on
  }, [clientId]);

  const formValid = () =>
    !requiredInvalid(values.fontName) &&
    !requiredInvalid(values.headerAlignment) &&
    NUMBER_FIELDS.every((field) => !numberInvalid(field, values.numbers[field]));

  const slots = (list: (string | null)[]) => Array.from({ length: ROUNDING_SLOTS }, (_, i) => list[i] || null);

  const save = () => {
    if (!formValid()) {
      setTouched((current) => ({ ...current, all: true }));
      // A bad (not missing) number says why; anything missing keeps the generic mandatory-fields toast.
      const mandatoryMissing =
        requiredInvalid(values.fontName) ||
        requiredInvalid(values.headerAlignment) ||
        NUMBER_FIELDS.some((field) => values.numbers[field].length === 0);
      const reason = mandatoryMissing
        ? null
        : NUMBER_FIELDS.map((field) => numberError(field, values.numbers[field])).find((message) => message !== null);
      if (reason) notifyError(reason);
      else validateForm(false);
      return;
    }

    const settings = {
      fontName: values.fontName,
      headerAlignment: values.headerAlignment,
      profitAndLossRoundingAccounts: slots(values.plRounding),
      balanceSheetRoundingAccounts: slots(values.bsRounding),
    } as Record<string, unknown>;
    for (const field of NUMBER_FIELDS) {
      settings[field] = Number(values.numbers[field]);
    }
    for (const field of FLAG_FIELDS) {
      settings[field] = !!values.flags[field];
    }

    const payload = settings as unknown as ReportFormatSettings;
    (clientId
      ? firmSettingsService.saveClientReportFormat(clientId, payload)
      : firmSettingsService.saveReportFormat(payload)
    ).then(
      (response) => {
        if (!mounted.current) return;
        apply(response);
        notifySuccess('Report format saved successfully');
      },
      (error) => notifyError(error),
    );
  };

  /** BUG-074: drops the client's own format so the firm's applies to its reports again. */
  const resetToFirm = () => {
    if (!clientId) return;
    firmSettingsService.resetClientReportFormat(clientId).then(
      (response) => {
        if (!mounted.current) return;
        apply(response);
        notifySuccess("Report format reset to the firm's default");
      },
      (error) => notifyError(error),
    );
  };

  /** Puts the last saved settings back. */
  const onDiscard = () => {
    discard();
    setTouched({ all: false, fields: new Set() });
  };

  /** APT233: replaces the saved settings with the defaults, so it asks first. */
  const onResetToDefault = () => {
    void openDialog<ConfirmPopupData, boolean>(ConfirmPopup, {
      data: {
        title: 'Reset to default',
        message: 'Reset every report format setting to its default value? Your current settings will be replaced.',
      },
    })
      .afterClosed()
      .then((confirmed) => {
        if (confirmed === true) resetToDefault();
      });
  };

  const resetToDefault = () => {
    firmSettingsService.resetReportFormat().then(
      (response) => {
        if (!mounted.current) return;
        setTouched({ all: false, fields: new Set() });
        apply(response);
        notifySuccess('Report format reset to default successfully');
      },
      (error) => notifyError(error),
    );
  };

  const markTouched = (name: string) =>
    setTouched((current) =>
      current.fields.has(name) ? current : { ...current, fields: new Set(current.fields).add(name) },
    );
  /** `ng-invalid ng-touched`. */
  const showInvalid = (name: string, invalid: boolean) => invalid && (touched.all || touched.fields.has(name));

  const setNumber = (field: NumberField, raw: string) =>
    setValues((current) => ({ ...current, numbers: { ...current.numbers, [field]: raw } }));
  const setFlag = (field: FlagField, checked: boolean) =>
    setValues((current) => ({ ...current, flags: { ...current.flags, [field]: checked } }));
  const setSlot = (key: 'plRounding' | 'bsRounding', index: number, value: string | null) =>
    setValues((current) => ({
      ...current,
      [key]: current[key].map((slot, i) => (i === index ? value : slot)),
    }));

  const numberField = (field: NumberField, label: string, suffix?: string) => {
    const message = showInvalid(field, numberInvalid(field, values.numbers[field]))
      ? numberError(field, values.numbers[field])
      : null;
    return (
      <div className="form-field">
        <p>{label}</p>
        <InputField
          fieldClassName="w-300"
          autoComplete="off"
          required
          maxLength={3}
          value={values.numbers[field]}
          invalid={showInvalid(field, numberInvalid(field, values.numbers[field]))}
          suffix={suffix ? <div className="sufix">{suffix}</div> : undefined}
          onChange={(event) => setNumber(field, event.target.value)}
          onBlur={() => markTouched(field)}
        />
        {message && (
          <p role="alert" className="font-size-12 text-danger-100 mt-5">
            {message}
          </p>
        )}
      </div>
    );
  };

  // 2 Nov design review: on/off options are toggle switches (still staged until Save, like every other field on this form).
  const flagBox = (field: FlagField, label: ReactNode) => (
    <Toggle checked={values.flags[field]} onCheckedChange={(on) => setFlag(field, on)}>
      {label}
    </Toggle>
  );

  const roundingBox = (title: string, key: 'plRounding' | 'bsRounding', accounts: RoundingAccountOption[]) => (
    <div className="pl-box">
      <div className="form-field hr-bottom pt-10 pb-5">
        <p className="font-size-16">{title}</p>
      </div>
      {values[key].map((slot, index) => (
        <div className="form-field" key={index}>
          <p>Select Accounts</p>
          <Select
            className="w-300"
            items={toOptions(accounts)}
            bindLabel="label"
            bindValue="value"
            value={slot}
            onChange={(value) => setSlot(key, index, (value as string | null) ?? null)}
          />
        </div>
      ))}
    </div>
  );

  const formattingTab = (
    <form onSubmit={(event) => event.preventDefault()}>
      <div className="sidenav-body pt-10">
        <div className="sidenav-body-content p-0">
          <div className="form-field-title pb-1">
            <p>Report Fonts and Heading</p>
          </div>
          <div className="d-flex gap-30">
            <div className="form-field">
              <p>Font Name</p>
              <Select
                className="w-300"
                items={fonts}
                value={values.fontName}
                clearable={false}
                onChange={(value) => setValues((current) => ({ ...current, fontName: value as string | null }))}
              />
            </div>
            {numberField('titleFontSize', 'Title Font Size', 'px')}
          </div>
          <div className="d-flex gap-30">
            {numberField('tableHeaderFontSize', 'Table Header Font Size', 'px')}
            {numberField('reportBodyFontSize', 'Report Body Font Size', 'px')}
          </div>
          <div className="d-flex gap-30">
            <div className="form-field">
              <p>Header Alignment</p>
              <Select
                className="w-300"
                items={headerAlignments}
                value={values.headerAlignment}
                clearable={false}
                onChange={(value) => setValues((current) => ({ ...current, headerAlignment: value as string | null }))}
              />
            </div>
            {numberField('headerFontSize', 'Header Font Size', 'px')}
          </div>
          <div className="d-flex gap-30">
            {numberField('noteBodyPadding', 'Padding for Note Body', 'px')}
            {numberField('textHeadingPadding', 'Padding for Text and Heading', 'px')}
          </div>

          <div className={SECTION_TITLE}>
            <p>Page Margins (PDF only)</p>
          </div>
          <div className="d-flex gap-30">
            {numberField('marginTop', 'Top', 'pt')}
            {numberField('marginBottom', 'Bottom', 'pt')}
          </div>
          <div className="d-flex gap-30">
            {numberField('marginLeft', 'Left', 'pt')}
            {numberField('marginRight', 'Right', 'pt')}
          </div>
          <div className="d-flex flex-column">
            {flagBox('printPageNumberInPdf', 'Print page number in pdf')}
            {flagBox('printPageNumberOnCoverPage', 'Print page number on cover page')}
            <p className="font-size-12 text-gray mt-5">
              These settings apply when downloading accounts as a PDF. Save these settings, then re-download the report
              to see the change.
            </p>
          </div>

          <div className={SECTION_TITLE}>
            <p>Formatting</p>
          </div>
          <div className="d-flex flex-column">
            {flagBox('doubleUnderlineTotals', 'Double underline on total figure')}
            {flagBox('showDashForZero', 'Show "-" in place of "0" value')}
            {flagBox('hidePeriodStartOnCoverPage', 'Hide Period Start date on Cover Page')}
            {flagBox('printRegistrationNumberOnBalanceSheet', 'Print Company Registration Number on Balance sheet')}
          </div>
        </div>
      </div>
    </form>
  );

  const otherTab = (
    <form onSubmit={(event) => event.preventDefault()}>
      <div className="sidenav-body pt-10">
        <div className="sidenav-body-content p-0">
          <div className="form-field-title pb-1">
            <p>Journal/Trial balance settings</p>
          </div>
          {/* APT259: stored and loaded identically to every other flag below, firm-wide or per client (BUG-074) -
              there is no firm-only restriction on this field, so it belongs here same as the rest. */}
          <div className="form-field">
            <p>Auto Save</p>
            {flagBox('autoSaveJournals', 'Auto save journal entries')}
          </div>

          <div className={SECTION_TITLE}>
            <p>Rounding Figures in the Accounts</p>
          </div>
          <div>{flagBox('manageAutoRounding', 'Manage auto rounding values')}</div>

          <div className="d-flex gap-30 mt-1">
            {roundingBox('Profit and Loss Accounts', 'plRounding', profitAndLossAccounts)}
            {roundingBox('Balance sheet Accounts', 'bsRounding', balanceSheetAccounts)}
          </div>
        </div>
      </div>
    </form>
  );

  // 2 Nov design review: the "This client's reports use..." line is one info icon; once a setting changed, Unsaved changes / Discard / Save sit
  // in the sticky page header at the top right (the fixed bottom footer is gone).
  const customisedNote = isCustomised
    ? "This client's reports use the format below instead of the firm's."
    : "This client's reports use the firm's report format. Change a setting and save to give this client its own.";
  const statusNote = clientId ? [intro, customisedNote].filter(Boolean).join('\n') : 'Change a setting, then click Save.';

  return (
    // Inside a client's settings frame the frame already scrolls: a second `.form` scroller there is the inner scroll bar seen in the review.
    <div className={clientId ? 'wp-100' : 'form wp-100'}>
      <div className="sidenav-details emoji-sec pt-5">
        {/* Both routes that render this page (the firm-wide Settings list and a client's own Settings list) already
            show "Report Format" as the highlighted item in their left-hand menu, so a second "Report Format" heading
            here would just repeat it - the header keeps its row (info hint, actions, close) with the title hidden. */}
        <PageHeader
          title="Report Format"
          hideTitle
          dirty={dirty}
          alwaysShowActions
          actionsPlacement="footer"
          onSave={save}
          onDiscard={onDiscard}
        >
          <InfoHint text={statusNote} />
          {clientId && isCustomised && (
            <Button className="action-button secondary-button" onClick={resetToFirm}>
              <Icon name="restart_alt" outlined />
              Use firm default
            </Button>
          )}
          {/* APT233: the firm's defaults can be restored without entering Edit first. */}
          {!clientId && (
            <Button className="action-button secondary-button" onClick={onResetToDefault}>
              Reset to default
            </Button>
          )}
          {onClose && (
            <Button className="close-btn" aria-label="Close" onClick={onClose}>
              <Icon name="close" />
            </Button>
          )}
        </PageHeader>
        <LeaveGuard when={dirty} />
        <div className="sidenav-body">
          <TabGroup
            alignTabs="start"
            tabs={[
              { label: 'Report Formatting', children: formattingTab },
              { label: 'Other Settings', children: otherTab },
            ]}
          />
        </div>
        <PageHeaderFooter dirty={dirty} alwaysShowActions onSave={save} onDiscard={onDiscard} />
      </div>
    </div>
  );
}
